import * as React from 'react';
import Modal from '@mui/material/Modal';
import { Box, Typography, IconButton, Tooltip, Popover } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import snakeToPascal from '../tools';
import { isFieldVisible } from '../utils/fieldVisibility';
import VariantStyles from "./Draggables/VariantStyles";

import StringField from './ModelDataFields/Input/String';
import FloatField from './ModelDataFields/Input/Float';
import IntField from './ModelDataFields/Input/Int';
import DateField from './ModelDataFields/Input/Date';
import EnumField from './ModelDataFields/Input/Enum';
import BoolField from './ModelDataFields/Input/Bool';
import TimeSerieField from './ModelDataFields/Input/TimeSerie';
import TimeSerieTimingField from './ModelDataFields/Input/TimeSerieTiming';
import RelationField from './ModelDataFields/Input/Relation';
import ListField from './ModelDataFields/Input/List';
import CrossField from './ModelDataFields/Input/CrossField';
import TimingReferenceField from './ModelDataFields/Input/TimingReference';

import OutputFloatField from './ModelDataFields/Output/Float';
import CustomizableTimingReferenceField from './ModelDataFields/Input/CustomizableTimingReference';
import OutputGridField from './ModelDataFields/Output/Grid';
import DynamicGridField from './ModelDataFields/Output/DynamicGrid';
import SensitivityFloatField from './ModelDataFields/Sensitivities/Float';
import SensitivityIntField from './ModelDataFields/Sensitivities/Int';
import SensitivityStringField from './ModelDataFields/Sensitivities/String';
import SensitivityDateField from './ModelDataFields/Sensitivities/Date';
import SensitivityBoolField from './ModelDataFields/Sensitivities/Bool';
import SensitivityCustomizableTimingReferenceField from './ModelDataFields/Sensitivities/CustomizableTimingReference';
import SensitivityTimeSerieField from './ModelDataFields/Sensitivities/TimeSerie';
import SensitivityEnumField from './ModelDataFields/Sensitivities/Enum';
import SensitivityRelationField from './ModelDataFields/Sensitivities/Relation';
import SensitivityTimingReferenceField from './ModelDataFields/Sensitivities/TimingReference';
import SensitivityTimeSerieTimingField from './ModelDataFields/Sensitivities/TimeSerieTiming';
import SensitivityListField from './ModelDataFields/Sensitivities/List';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import ModelDataDocumentationModal from './ModelDataDocumentationModal';
import OutputTimeSeriesGrid from './ModelDataFields/Output/TimeSeriesGrid';
import InputModelDataValueCommentPopover from './ModelDataFields/Input/reusables/CommentsPopover';
import FieldExplainButton from './ModelDataFields/Input/reusables/FieldExplainButton';
import OutputTimeSeriesGridOperational from './ModelDataFields/Output/TimeSeriesGridOperational';
import FieldBindingModal from './FieldBindingModal';
import { useAppStore } from '../shared/stores/useAppStore';
import { useEditorStore } from '../shared/stores/useEditorStore';
import { useScenarioTree } from '../shared/hooks/queries/useScenarioTree';
import { useUpdateFieldValue } from '../shared/hooks/mutations/useUpdateFieldValue';
import ErpBindingButton, { ErpBinding } from './ModelDataFields/Input/reusables/ErpBindingButton';
import apiService from '../services/ApiService';

const modalStyle = {
    position: 'absolute' as 'absolute',
    top: '53%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '90%',
    height: '88%',
    bgcolor: 'background.paper',
    borderRadius: 2,
    overflow: 'hidden',
    overflowY: 'scroll',
    // display: 'flex',
    // border: '2px solid #000',
    // boxShadow: 24,
    // p: 4,
  };

const SensitivityFieldByType = {
    'str': SensitivityStringField,
    'int': SensitivityIntField,
    'float': SensitivityFloatField,
    'datetime': SensitivityDateField,
    'enum': SensitivityEnumField,
    'bool': SensitivityBoolField,
    'list': SensitivityListField,
    'time_serie_timing': SensitivityTimeSerieTimingField,
    'time_serie': SensitivityTimeSerieField,
    'relation': SensitivityRelationField,
    'timing_reference': SensitivityTimingReferenceField,
    'customizable_timing_reference': SensitivityCustomizableTimingReferenceField,
}

const FieldByKindAndType = {
    'Input': {
        'str': StringField,
        'int': IntField,
        'float': FloatField,
        'datetime': DateField,
        'enum': EnumField,
        'bool': BoolField,
        'list': ListField,
        'time_serie_timing': TimeSerieTimingField,
        'time_serie': TimeSerieField,
        'relation': RelationField,
        'timing_reference': TimingReferenceField,
        'customizable_timing_reference': CustomizableTimingReferenceField,
    },
    'Output': {
        'float': OutputFloatField,
        'grid': OutputGridField,
        'dynamic_grid': DynamicGridField,
    }
}

// Field types that can be bound to ERP values (simple value types only)
const ERP_BINDABLE_TYPES = ['str', 'int', 'float', 'datetime', 'bool'];

const FieldDescriptionPopover = ({ field }) => {

    const [anchorEl, setAnchorEl] = React.useState(null);

    const handleClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);

    return (
        <div>
            <Tooltip title='Details'>
                <IconButton
                    edge="end"
                    color="inherit"
                    size='small'
                    sx={{
                        marginRight: 1,
                        marginLeft: 0,
                        marginTop: 0.5,
                        height: 20,
                        width: 20,
                    }}
                    onClick={(e) => {
                        setAnchorEl(e.currentTarget);
                    }}
                >
                    <span className="material-symbols-outlined text-[18px] text-slate-400">help_outline</span>
                </IconButton>
            </Tooltip>
            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
                }}
            >
                <Box sx={{
                    height: "fit-content",
                    width: "fit-content",
                    overflow: 'hidden',
                    overflowY: 'scroll'
                }}>
                    <Box sx={{ marginTop: 1, marginBottom: 1 }}>
                    {
                        field.description.split('\n').map(str => (
                            <Typography fontSize={16} sx={{ paddingLeft: 1, paddingRight: 1 }}>
                                {str}
                            </Typography>
                        ))
                    }
                    </Box>
                </Box>
            </Popover>
        </div>
    )
}

export default function ModelDataPanel({ open, setOpen, item, variant, currentSensitivity, scenarioId }) {
    const hasErpConnections = useAppStore(s => s.hasErpConnections);
    const storeScenarioId = useEditorStore(s => s.scenarioId) ?? 0;
    const { state: currentState } = useScenarioTree(storeScenarioId);
    const updateField = useUpdateFieldValue();

    const crossFieldObj = {};

    const [mounted, setMounted] = React.useState(false);

    const [docModalOpen, setDocModalOpen] = React.useState(false);

    const [bindingModalOpen, setBindingModalOpen] = React.useState(false);
    const [bindingField, setBindingField] = React.useState<{ name: string; field: any } | null>(null);
    const [bindingRefreshTrigger, setBindingRefreshTrigger] = React.useState(0);
    const [existingBinding, setExistingBinding] = React.useState<ErpBinding | null>(null);

    const handleOpenBindingModal = (name: string, field: any, binding: ErpBinding | null) => {
        setBindingField({ name, field });
        setExistingBinding(binding);
        setBindingModalOpen(true);
    };

    const handleBindingCreated = () => {
        setBindingRefreshTrigger(prev => prev + 1);
    };

    const reloadLocalState = () => {
        setMounted(!mounted);
    }

    const handleSyncApply = async (field: any, erpValue: any) => {
        // Format value based on field type
        let formattedValue = erpValue;

        if (field.type === 'datetime' && erpValue) {
            // For datetime, format as YYYY-MM-DD string for the API
            if (typeof erpValue === 'object' && erpValue.format) {
                formattedValue = erpValue.format('YYYY-MM-DD');
            } else if (typeof erpValue === 'string') {
                formattedValue = erpValue;
            }
        } else if (field.type === 'float' && typeof erpValue === 'string') {
            // For float, replace comma with dot
            formattedValue = erpValue.replace(',', '.');
        }

        updateField.mutate(
            { id: field.id, value: formattedValue, scenarioId: storeScenarioId },
            {
                onSuccess: () => {
                    field.currentValue.value = erpValue;
                    reloadLocalState();
                },
            }
        );
    };

    const renderField = (name: string, field: any) => {
        
        if (item.sensitivityModelData == null) {
            try {
                const FieldToRender = FieldByKindAndType[item.kind][field.type];
                field.model_data_id = item.id;

                if (FieldToRender) {
                    return (
                        <Box display='flex' flexDirection='column' marginBottom={4}>
                            <Box display='flex' flexDirection='row' alignItems='center' marginRight={2} marginBottom={1}>
                                {
                                    field.description ?
                                    <FieldDescriptionPopover field={field} /> : <Box sx={{ height: 20, width: 20, marginLeft: 0, marginRight: 1 }} />
                                }
                                <Typography fontSize={18}>
                                    {field.display_name ? field.display_name : name}
                                </Typography>
                                <FieldExplainButton field={field} name={name} />
                                {
                                    item.kind == 'Input' ?
                                    <Box marginLeft={0.5}>
                                        <InputModelDataValueCommentPopover field={field}/>
                                    </Box> : null
                                }
                                {hasErpConnections && item.kind === 'Input' && ERP_BINDABLE_TYPES.includes(field.type) && (
                                    <Box sx={{ marginLeft: -0.5 }}>
                                        <ErpBindingButton
                                            onClick={(binding) => handleOpenBindingModal(name, field, binding)}
                                            fieldId={field.id}
                                            refreshTrigger={bindingRefreshTrigger}
                                            currentValue={field.currentValue?.value}
                                            appFieldType={field.type}
                                            onSyncApply={(erpValue) => handleSyncApply(field, erpValue)}
                                        />
                                    </Box>
                                )}
                            </Box>
                            <FieldToRender name={name} field={field} siblingFields={item.fields} onFieldChange={reloadLocalState} />
                        </Box>
                    )
                }
                else {
                    if (field.type === 'cross_field') {
                        if (crossFieldObj[field.model_data_type]) {
                            crossFieldObj[field.model_data_type].push(field.field_name);
                        } else {
                            crossFieldObj[field.model_data_type] = [field.field_name];
                        }
                    }
                    return (
                        <div></div>
                    )
                }
            } catch (e) {
                console.log(e);
                return (
                    <div></div>
                )
            }
        }
         else {
            try {

                const SensitivityFieldToRender = SensitivityFieldByType[field.type];
                field.model_data_id = item.id;

                if (SensitivityFieldToRender) {
                    return (
                        <Box display='flex' flexDirection='column' marginBottom={3} key={`${item.id}-${field.name}-sensitivity`}>
                            <Typography fontSize={18} marginRight={2} marginBottom={1}>
                                {name}
                            </Typography>
                            <SensitivityFieldToRender name={name} item={item} field={field} currentSensitivity={currentSensitivity} />
                        </Box>
                    )
                }
                else {
                    return (<div></div>)
                }
            } catch (e) {
                console.log(e);
                return (
                    <div></div>
                )
            }
        }
    }

    const handleClose = (event, reason?) => {
        if (reason !== 'escapeKeyDown') {
            setOpen(false);
            event.stopPropagation();
        }
    }

    return (
        <Modal
          open={open}
          onClose={handleClose}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
          sx= {{ display: "flex"}}
        >
            <Box sx={modalStyle}>
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'right'
                }}>
                    <IconButton
                        color="inherit"
                        size='small'
                        sx={{ marginRight: 1, marginTop: 1, align: 'left', display: 'flex', justifyContent: 'left' }}
                        onClick={(e) => {
                            handleClose(e)
                        }}
                    >
                        <CloseIcon fontSize="small"/>
                    </IconButton>
                </Box>
                <Typography id="modal-modal-title" variant="body1" align="center" component="h2" marginTop={-2} fontSize={22}>
                {item.name}
                </Typography>
                <Box sx={{
                    display: 'flex',
                    flexDirection: "row",
                    marginTop: 2,
                }}>
                    <Typography 
                        variant="body1"
                        align="center"
                        component="h2"
                        fontSize={18}
                        marginLeft={5}
                    >
                    Type : 
                    </Typography>
                    <Box sx={{
                            marginLeft: 2,
                            marginTop: 0.5,
                            backgroundColor: VariantStyles[variant]["typecolor"],
                            borderRadius: 2,
                            height: 20
                        }}>
                        <Typography
                            noWrap
                            marginLeft={2}
                            marginRight={2}
                            color="white"
                            fontSize={14}>
                            {snakeToPascal(item.type)}
                        </Typography>
                    </Box>
                    {
                    item.documentation ?
                        <Tooltip title='More Details'>
                            <IconButton
                                edge="end"
                                color="inherit"
                                size='small'
                                sx={{
                                    marginLeft: 1,
                                }}
                                onClick={() => {
                                    setDocModalOpen(true);
                                }}
                            >
                                <HelpOutlineOutlinedIcon fontSize="small" />
                            </IconButton>
                        </Tooltip> : null
                    }
                </Box>
                <Typography 
                        variant="body1"
                        // align="center"
                        component="h2"
                        fontSize={18}
                        marginTop={3}
                        marginLeft={5}
                    >
                    Fields : 
                </Typography>
                {/* <LocalizationProvider dateAdapter={AdapterDayjs}> */}
                    <Box sx={{
                        // display: 'flex',
                        marginTop: 1,
                        marginLeft: 10,
                        marginBottom: 5
                        // overflow: 'hidden',
                        // overflowY: 'scroll',
                        // height: 450,
                    }}>
                        {Object.entries(item.fields).filter(([_, field]: any) => isFieldVisible(field, item.fields)).map(([name, field]) => renderField(snakeToPascal(name), field))}
                        {
                            currentState['Input'] ?
                            <CrossField crossFieldObj={crossFieldObj} /> : <div></div>
                        }
                        {
                            item.kind == 'Output' && currentState['Output'].field_values ?
                            <OutputTimeSeriesGrid fields={item.fields} /> : null
                        }
                        {
                            item.kind == 'Output' && currentState['Output'].field_values === undefined ?
                            <OutputTimeSeriesGridOperational fields={item.fields} /> : null
                        }
                    </Box>
                {/* </LocalizationProvider> */}
            <ModelDataDocumentationModal item={item} variant={variant} open={docModalOpen} setOpen={setDocModalOpen}/>
            {bindingField && (
                <FieldBindingModal
                    open={bindingModalOpen}
                    setOpen={setBindingModalOpen}
                    fieldName={bindingField.name}
                    field={bindingField.field}
                    modelDataId={item.id}
                    modelDataName={item.name}
                    scenarioId={scenarioId}
                    existingBinding={existingBinding}
                    onBindingCreated={handleBindingCreated}
                    currentValue={bindingField.field.currentValue?.value}
                    onSyncApply={(erpValue) => handleSyncApply(bindingField.field, erpValue)}
                />
            )}
            </Box>
        </Modal>
    )

}

export { FieldByKindAndType, FieldDescriptionPopover }
